/**
 * Ninety days, one cell each, oldest on the left.
 *
 * A day without a rollup is drawn in the unknown colour and never borrows a
 * neighbour's - see `uptimeStrip` in `lib/monitoringView.ts` for why that is a
 * claim about the data and not a styling choice. Each cell carries its own
 * numbers in a tooltip, and the strip as a whole has a text summary for a
 * screen reader, which cannot hover ninety slivers.
 *
 * On a phone the strip keeps the last 30 days: ninety cells in 340 pixels are
 * under three pixels each, which is a texture rather than a chart. The older
 * sixty are hidden by CSS rather than dropped, so the numbers do not change
 * with the window.
 */
import { cn } from "@wa/ui";
import {
  formatUptime,
  tallinnDayKey,
  uptimeStrip,
  type MonitorDayView,
  type StripTone,
} from "../../../lib/monitoring";
import { fmtDate } from "../../ui";
import { durationText, type T } from "./words";

const DAYS = 90;
/** How many of the newest days a phone-width strip keeps. */
const NARROW_DAYS = 30;

const FILL: Record<StripTone, string> = {
  up: "var(--color-success)",
  partial: "var(--color-warning)",
  down: "var(--color-destructive)",
  unknown: "color-mix(in oklab, var(--color-muted-foreground) 22%, transparent)",
};

export function UptimeStrip({ days, t }: { days: MonitorDayView[]; t: T }) {
  const cells = uptimeStrip(days, tallinnDayKey(new Date()), DAYS);
  const counted = cells.filter((c) => c.tone !== "unknown");
  const bad = cells.filter((c) => c.tone === "down" || c.tone === "partial").length;

  const tip = (day: string, row: MonitorDayView | null) => {
    const date = fmtDate(`${day}T12:00:00Z`);
    if (!row || row.uptime === null) return `${date}: ${t("monitoring.strip.noData")}`;
    const down = row.downSeconds > 0 ? ` - ${t("monitoring.strip.downFor", { time: durationText(t, row.downSeconds) })}` : "";
    return `${date}: ${formatUptime(row.uptime)}${down}`;
  };

  return (
    <div>
      <div
        className="flex h-9 items-stretch gap-[2px]"
        role="img"
        aria-label={t("monitoring.strip.summary", { n: counted.length, bad })}
      >
        {cells.map((cell, i) => (
          <span
            key={cell.day}
            title={tip(cell.day, cell.row)}
            className={cn("min-w-0 flex-1 rounded-[3px]", i < DAYS - NARROW_DAYS && "max-sm:hidden")}
            style={{ background: FILL[cell.tone] }}
          />
        ))}
      </div>
      <div className="crm-mut mt-2 flex justify-between text-[11.5px]">
        <span className="max-sm:hidden">{t("monitoring.strip.ago", { n: DAYS })}</span>
        <span className="sm:hidden">{t("monitoring.strip.ago", { n: NARROW_DAYS })}</span>
        <span>{t("monitoring.strip.today")}</span>
      </div>
      <div className="crm-mut mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11.5px]">
        {(["up", "partial", "down", "unknown"] as const).map((tone) => (
          <span key={tone} className="inline-flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-[3px]" style={{ background: FILL[tone] }} />
            {t(`monitoring.strip.legend.${tone}`)}
          </span>
        ))}
      </div>
    </div>
  );
}
